/**
 * Support ticket helpers — dialog collects details; mailto opens compose with plain text.
 * (mailto cannot render HTML/bold; section titles use plain emphasis.)
 */

export const SUPPORT_EMAIL = 'support@orbius.com';

export const SUPPORT_ISSUE_TYPES = [
  'Installation support',
  'Service line creation and UAT',
  'Service Line Management',
  'Service Line Troubleshooting',
  'Portal access and use',
  'Others',
] as const;

export type SupportIssueType = (typeof SUPPORT_ISSUE_TYPES)[number];

export interface SupportTicketFormValues {
  company?: string;
  serviceLineNumbers?: string;
  terminalId?: string;
  issueTypes?: SupportIssueType[];
  description?: string;
  subject?: string;
  to?: string;
}

export interface MailtoOptions {
  to?: string;
  subject?: string;
  body?: string;
}

/** Build a mailto: URL with percent-encoded query params (%20, not +). */
export function buildMailtoHref({
  to = SUPPORT_EMAIL,
  subject,
  body,
}: MailtoOptions): string {
  const parts: string[] = [];
  if (subject) parts.push(`subject=${encodeURIComponent(subject)}`);
  if (body) parts.push(`body=${encodeURIComponent(body)}`);
  return parts.length > 0 ? `mailto:${to}?${parts.join('&')}` : `mailto:${to}`;
}

function line(value?: string): string {
  return value?.trim() ? value.trim() : '';
}

function checkboxRow(label: string, checked: boolean): string {
  return `[${checked ? 'x' : ' '}] ${label}`;
}

/** Plain-text support ticket body for the mail client. */
export function buildSupportTicketBody(values: SupportTicketFormValues = {}): string {
  const selected = new Set(values.issueTypes ?? []);

  return [
    'Hello Orbius Support,',
    '',
    'Please assist with the following request.',
    '',
    '────────────────────────────────────────',
    'SUPPORT TICKET',
    '────────────────────────────────────────',
    '',
    '1) Account and related resources information:',
    `Company (operator): ${line(values.company)}`,
    `Service line number(s): ${line(values.serviceLineNumbers)}`,
    `Terminal ID / kit serial: ${line(values.terminalId)}`,
    '',
    '2) Issue type — mark at least one with [x]:',
    ...SUPPORT_ISSUE_TYPES.map((type) => checkboxRow(type, selected.has(type))),
    '',
    '3) Ticket description:',
    line(values.description) || 'Do not hesitate to attach images/files to facilitate support.',
    '',
    '────────────────────────────────────────',
    '',
    'Thank you,',
    '',
  ].join('\n');
}

export function buildSupportTicketMailtoHref(values: SupportTicketFormValues = {}): string {
  return buildMailtoHref({
    to: values.to ?? SUPPORT_EMAIL,
    subject: values.subject ?? 'Support ticket',
    body: buildSupportTicketBody(values),
  });
}

/** Open the default mail client with the completed support ticket. */
export function openSupportTicketEmail(values: SupportTicketFormValues): void {
  window.location.href = buildSupportTicketMailtoHref(values);
}
